import { computed, h, reactive } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';
import { energyHeading, priceHeading } from '@/composables/feedUnitHeadings';
import { locationFacetValue, locationLabel } from '@/composables/records/recordsFormat';
import { useGroupedWeeklyAggregation, type GroupedWeeklyRow } from '@/composables/records/useGroupedWeeklyAggregation';
import { BLANK_FACET_VALUE, type RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { useRecordsPage } from '@/composables/records/useRecordsPage';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import { store } from '@/plugins/store';
import { queryKeys, useFeedDeliveriesApproved, useFeedDeliveriesOriginal, useFeedDeliveriesWeekly } from '@/plugins/store/actions/queries/feedDeliveries';
import { RECORD_ACTIONS_COLUMN_ID, type ApprovedFeedDeliveryRow, type OriginalFeedDeliveryRow, type RecordsListFetchParams } from '@/types';
import { TOGGLE_COLUMN_IDS, type FeedDeliveriesToggleColumnId } from './useFeedDeliveriesColumnVisibility';

const HEADING_KEYS: Record<FeedDeliveriesToggleColumnId, string> = {
    feed_category: 'views.pigs.records.table.headings.feedCategory',
    created_by: 'views.pigs.records.table.headings.createdBy',
};

/**
 * What the Approved table filters on: the barn a delivery was matched to (mirrors Sold's own single
 * location facet - see `useSoldPage`), and the Feed Mix it was matched to. Both are client-side, over
 * the whole loaded period, the same as every other records page's header facets - see
 * `useRecordsAggregation`.
 */
function approvedFacets(): RecordFacetDefinitions<ApprovedFeedDeliveryRow> {
    const blank = i18n.global.t('views.pigs.records.table.texts.blank');

    return {
        barn: {
            value: (row) => locationFacetValue(row.farm, row.barn),
            label: (row) => locationLabel(row.farm, row.barn) || blank,
        },
        feed_mix_name: {
            value: (row) => row.feed_mix_id,
            label: (row) => row.feed_mix_name,
        },
        feed_category: {
            value: (row) => row.feed_mix_category_id ?? BLANK_FACET_VALUE,
            label: (row) => row.feed_mix_category_name ?? blank,
        },
    };
}

/**
 * Splits a translated "Label (unit)" heading into its label and its parenthetical unit, for a header
 * that renders the two as separate lines. Falls back to treating the whole heading as the label when
 * there is no parenthetical to split off.
 */
function splitUnitHeading(heading: string): { label: string; unit: string | null } {
    const match = /^(.*?)\s*(\([^()]*\))\s*$/.exec(heading);

    return match ? { label: match[1], unit: match[2] } : { label: heading, unit: null };
}

/**
 * A right-aligned column header rendered as two lines - the label, then its unit in the same muted
 * small text `ProfileMenu`'s own two-line user block uses. `FacetedDataTable` renders whatever
 * `ColumnDef.header` holds through `FlexRender`, which accepts a render function exactly like the one
 * returned here alongside the plain-string headings every other column still uses.
 */
function unitHeader(heading: string) {
    const { label, unit } = splitUnitHeading(heading);

    if (unit === null) {
        return () => h('div', { class: 'text-right' }, label);
    }

    return () => h('div', { class: 'text-right' }, [h('div', label), h('div', { class: 'text-xs text-gray-500' }, unit)]);
}

/**
 * The Energy and Price column headings, unit included, each rendered on its own line under the
 * label. The unit resolution itself (site-configurable display text, falling back to the FU/MJ
 * static text the directory's energy unit implies) is shared with `MixTable.vue`'s identical
 * per-100kg nutrient figures via `feedUnitHeadings` - only the i18n key namespace differs here, and
 * only this table always renders on two lines (`MixTable.vue` only does when its Category column is
 * crowding the row).
 */
function energyHeaderText() {
    return energyHeading((key) => i18n.global.t(`views.pigs.records.table.headings.${key}`));
}

function priceHeaderText() {
    return priceHeading((key) => i18n.global.t(`views.pigs.records.table.headings.${key}`));
}

/**
 * The Approved table's columns, in the order the table reads: when the delivery happened, where it
 * went, who delivered it, which Feed Mix it was matched to, how much, and its nutrient figures,
 * followed by the shared records kit's edit action. Price is dropped outright rather than rendered
 * blank when the caller cannot read it - the same choice the legacy Deliveries page makes for the
 * same right.
 *
 * Protein and Phosphorus each hide entirely when nothing in the full loaded set carries one - the
 * same `rows.some(...)` presence check `useDeathsPage`'s own `columns` uses for its own sparse
 * columns - rather than rendering a column of dashes for a directory that never records them.
 */
function approvedColumns(canReadPrices: boolean, rows: ApprovedFeedDeliveryRow[], isVisible: (columnId: FeedDeliveriesToggleColumnId) => boolean): ColumnDef<ApprovedFeedDeliveryRow>[] {
    const hasProtein = rows.some((row) => row.protein !== null);
    const hasPhosphorus = rows.some((row) => row.phosphorus !== null);

    const columns: ColumnDef<ApprovedFeedDeliveryRow>[] = [
        { id: 'date', accessorKey: 'date', header: i18n.global.t('views.pigs.records.table.headings.date'), enableSorting: true },
        { id: 'barn', header: i18n.global.t('views.pigs.records.table.headings.location'), meta: { filterable: true } },
        { id: 'supplier', accessorKey: 'supplier', header: i18n.global.t('views.pigs.records.table.headings.supplier'), enableSorting: true },
        { id: 'feed_mix_name', accessorKey: 'feed_mix_name', header: i18n.global.t('views.pigs.records.table.headings.feedMix'), meta: { filterable: true } },
        // Reuses `RecordRow`'s own `number` field for quantity - see `mapApprovedFeedDeliveryRow` -
        // which is also what gets it the shared table's grouped decimal formatting for free.
        { id: 'number', accessorKey: 'number', header: i18n.global.t('views.pigs.records.table.headings.quantity'), enableSorting: true, meta: { align: 'right' } },
    ];

    if (canReadPrices) {
        columns.push({ id: 'price', accessorKey: 'price', header: unitHeader(priceHeaderText()), enableSorting: true, meta: { align: 'right' } });
    }

    columns.push({ id: 'energy', accessorKey: 'energy', header: unitHeader(energyHeaderText()), enableSorting: true, meta: { align: 'right' } });

    if (hasProtein) {
        columns.push({ id: 'protein', accessorKey: 'protein', header: i18n.global.t('views.pigs.records.table.headings.protein'), enableSorting: true, meta: { align: 'right' } });
    }

    if (hasPhosphorus) {
        columns.push({ id: 'phosphorus', accessorKey: 'phosphorus', header: i18n.global.t('views.pigs.records.table.headings.phosphorus'), enableSorting: true, meta: { align: 'right' } });
    }

    if (isVisible('feed_category')) {
        columns.push({ id: 'feed_category', accessorKey: 'feed_mix_category_name', header: i18n.global.t(HEADING_KEYS.feed_category), enableSorting: true, meta: { filterable: true } });
    }

    if (isVisible('created_by')) {
        columns.push({ id: 'created_by', accessorKey: 'created_by', header: i18n.global.t(HEADING_KEYS.created_by), enableSorting: true });
    }

    // Opens the delivery's registration in edit mode, same as every other Records page - see
    // `useRecordFormModal`/`RecordsTable`. Not offered on the Original table: its rows are the
    // raw `FeedDelivery` entity, not a `FctObject`, so there is no single registration to edit.
    columns.push({ id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } });

    return columns;
}

/**
 * The Original table's columns: the raw supplier-reported fields, before any Feed Mix match. Mirrors
 * the legacy Leverancedata table's column set (materialenavn, materiale nr., mængde, gård) rather
 * than the Approved table's Feed Mix-oriented columns - there is no Feed Mix match yet at this stage.
 */
function originalColumns(): ColumnDef<OriginalFeedDeliveryRow>[] {
    return [
        { id: 'date', accessorKey: 'date', header: i18n.global.t('views.pigs.records.table.headings.date'), enableSorting: true },
        { id: 'supplier', accessorKey: 'supplier', header: i18n.global.t('views.pigs.records.table.headings.supplier'), enableSorting: true },
        { id: 'material_name', accessorKey: 'material_name', header: i18n.global.t('views.pigs.records.table.headings.feed') },
        { id: 'material_number', accessorKey: 'material_number', header: i18n.global.t('views.pigs.records.table.headings.materialNumber'), meta: { align: 'right' } },
        { id: 'number', accessorKey: 'number', header: i18n.global.t('views.pigs.records.table.headings.quantity'), enableSorting: true, meta: { align: 'right' } },
        { id: 'farm_name', accessorKey: 'farm_name', header: i18n.global.t('views.pigs.records.table.headings.farm'), enableSorting: true },
    ];
}

/** The Original table offers no header facets - there is nothing on its rows worth narrowing by yet. */
function noFacets() {
    return {};
}

/**
 * The Deliveries page's configuration of the shared records kit, doubled: one `useRecordsPage` per
 * table, since each reads its own endpoint, plus the chart's own aggregation reading a third.
 *
 * The two table instances are kept in lock-step by routing every week-selection call through this
 * composable's own `selectWeek`/`resetSelection` rather than letting either call its own - a click
 * on the chart has to scope both tables at once, and nothing else here ever calls either instance's
 * version directly, so they can never drift apart. `selectedWeekStart` is read back off the
 * Approved instance for the same reason picking either would do: both move together.
 */
export function useFeedDeliveriesPage() {
    const fetchParams = computed<RecordsListFetchParams>(() => ({
        period: store.getters.getSelectedPeriodByDate,
        locationIds: store.getters.getSelectedSiteIdsFilteredByPigType,
    }));
    const period = computed(() => fetchParams.value.period);
    const isQueryEnabled = computed(() => fetchParams.value.locationIds.length > 0);

    const { data: weeklyData, isError: isWeeklyError, refetch: refetchWeekly } = useFeedDeliveriesWeekly(fetchParams, { enabled: isQueryEnabled });
    const weeklyRows = computed(() => weeklyData.value ?? []);

    // One series per Feed Mix, valued by the quantity delivered
    const groupedRows = computed<GroupedWeeklyRow[]>(() => weeklyRows.value.map((row) => ({ week_start: row.week_start, id: row.feed_mix_id, name: row.feed_mix_name, value: row.quantity })));
    const { ranking, selectedIds: selectedFeedMixIds, series, toggle: toggleFeedMix } = useGroupedWeeklyAggregation(groupedRows, period);

    const feedMixesLabel = computed(() => i18n.global.t('views.feed.deliveriesV2.feedMixes.label').replace('%SHOWN%', String(selectedFeedMixIds.value.size)).replace('%TOTAL%', String(ranking.value.length)));

    const canReadPrices = computed(() => Boolean(store.getters.loggedInUserCanReadFeedPrices));

    const approvedPage = useRecordsPage<ApprovedFeedDeliveryRow>({
        records: { use: useFeedDeliveriesApproved, rootKey: queryKeys.getFeedDeliveriesApprovedRoot },
        facets: approvedFacets,
    });

    const originalPage = useRecordsPage<OriginalFeedDeliveryRow>({
        records: { use: useFeedDeliveriesOriginal, rootKey: queryKeys.getFeedDeliveriesOriginalRoot },
        facets: noFacets,
    });

    // Feed category and Created by are always offered and off by default. Approved only - the
    // Original table has neither.
    //
    // Hiding a column drops its own active filter selection: a hidden column has no chip left to
    // clear it from, and the facet filter would otherwise keep matching rows invisibly.
    function clearFilterIfHidden(id: FeedDeliveriesToggleColumnId) {
        if (id in approvedPage.columnFilters.value) {
            const next = { ...approvedPage.columnFilters.value };

            delete next[id];
            approvedPage.columnFilters.value = next;
        }
    }

    const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<FeedDeliveriesToggleColumnId>({
        storageKey: 'feed.deliveries.approved.columnVisibility',
        columnIds: TOGGLE_COLUMN_IDS,
        isAvailable: () => true,
        defaultVisible: () => false,
        label: (id) => i18n.global.t(HEADING_KEYS[id]),
        onHide: clearFilterIfHidden,
    });

    function selectWeek(weekStart: string) {
        approvedPage.selectWeek(weekStart);
        originalPage.selectWeek(weekStart);
    }

    function resetSelection() {
        approvedPage.resetSelection();
        originalPage.resetSelection();
    }

    // Reactive rather than a plain spread: a plain object holding these refs as properties would
    // not auto-unwrap them in the template the way a top-level `<script setup>` binding does, and
    // `v-model` on a nested prop needs both directions to work.
    const approved = reactive({
        ...approvedPage,
        columns: computed(() => approvedColumns(canReadPrices.value, approvedPage.rows.value, isVisible)),
        columnToggleOptions,
        toggleColumn,
        setAllColumns,
        // Quantity only for now - see `original` below for the same `RecordTotals.totalPigs`
        // trick. Energy/protein/phosphorus are per-100kg normalised figures, not additive across
        // rows, so summing them would be meaningless; omitted from the totals row until there is a
        // real weighted-average (or similar) figure to show instead.
        totalsSubheader: computed(() => ({
            date: i18n.global.t('views.feed.deliveriesV2.approved.totals'),
            number: i18n.global.n(approvedPage.totals.value.totalPigs, 'decimal'),
        })),
    });

    const original = reactive({
        ...originalPage,
        columns: computed(() => originalColumns()),
        // Quantity is `RecordTotals.totalPigs` under the hood - see the `number` column above -
        // read back here under the page's own label rather than the shared pig-domain one.
        totalsSubheader: computed(() => ({
            date: i18n.global.t('views.feed.deliveriesV2.original.totals'),
            number: i18n.global.n(originalPage.totals.value.totalPigs, 'decimal'),
        })),
    });

    // Each table shows its own loading state independently - see the `isFetching` on `approved`/
    // `original` themselves - so only the error state is combined: one banner replaces the whole
    // page regardless of which of the three fetches failed, the same as every other records page's
    // single-fetch error banner.
    const isError = computed(() => isWeeklyError.value || approvedPage.isError.value || originalPage.isError.value);

    function refetch() {
        refetchWeekly();
        approvedPage.refetch();
        originalPage.refetch();
    }

    return {
        ranking,
        series,
        selectedFeedMixIds,
        toggleFeedMix,
        feedMixesLabel,
        selectedWeekStart: approvedPage.selectedWeekStart,
        selectWeek,
        resetSelection,
        isError,
        refetch,
        approved,
        original,
    };
}
